import { motion } from "framer-motion";
import { Compass, Cpu, Gem, Handshake, MapPin } from "lucide-react";
import { SITE } from "../config/site";
import { KineticText } from "../components/KineticText";
import { ParticleField } from "../components/ParticleField";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";

const PRINCIPLES = [
  { num: "01", icon: Gem, title: "Craft Over Speed — Then Both", text: "We refuse the false choice between fast and excellent. Senior teams, proven systems and reusable architecture let us ship quickly without cutting corners." },
  { num: "02", icon: Cpu, title: "Technology Serves the Story", text: "The stack is never the hero — your product is. We pick boring, battle-tested tools for the core and reserve the exotic for where it creates real advantage." },
  { num: "03", icon: Handshake, title: "Partners, Not Vendors", text: "Weekly demos, open backlogs and honest advice — even when it means recommending a smaller build. Your win compounds into ours." },
];

const TIMELINE = [
  { year: "2024", title: "The First Spark", text: "SparkGrid Solutions is founded in Mumbai — a small team with one stubborn belief: Indian studios can set global design benchmarks. First website and app projects ship within months." },
  { year: "2025", title: "The Grid Expands", text: "Fifty clients strong across fintech, real estate and healthcare. Our Web3 practice launches, shipping smart contracts and dApps on Ethereum, Polygon and BSC." },
  { year: "2026", title: "The Hundred Club", text: "We cross 100+ clients and 120+ shipped projects — from conversion-focused websites to Solana dApps and custom AI-powered platforms." },
  { year: "Next", title: "SparkGrid Lab", text: "Now building our in-house product lab: AI tooling and crypto infrastructure that turn two years of client learnings into products of our own." },
];

const TEAM = [
  { icon: Compass, role: "Founder & Chief Executive", focus: "Strategy, partnerships and the occasional production hotfix." },
  { icon: Cpu, role: "Chief Technology Officer", focus: "Architecture, security reviews and engineering culture." },
  { icon: Gem, role: "Head of Design", focus: "Brand systems, motion language and pixel-level obsession." },
  { icon: Handshake, role: "Engineering Lead", focus: "Delivery, code quality and mentoring the next grid of engineers." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      {/* HERO */}
      <section className="grain relative overflow-hidden bg-night pb-20 pt-36 lg:pb-28 lg:pt-44">
        <ParticleField className="absolute inset-0 h-full w-full" />
        <div className="pointer-events-none absolute -right-32 top-0 h-80 w-80 rounded-full bg-ember/15 blur-[110px]" />
        <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8 lg:text-left">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-spark"
          >
            About Us · Est. Mumbai
          </motion.p>
          <h1 className="mt-6 font-syne text-4xl font-extrabold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="about-headline">
            <KineticText
              lines={[
                "MUMBAI MADE.",
                <span key="g" className="bg-gradient-to-r from-spark to-ember bg-clip-text text-transparent">GLOBALLY</span>,
                "TRUSTED.",
              ]}
              delay={0.25}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8 }}
            className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg lg:mx-0"
          >
            {SITE.name} is a software studio from Mumbai. We exist for one reason:
            to prove that world-class digital products don't need a Silicon Valley postcode —
            they need obsession, honesty and craft.
          </motion.p>
        </div>
      </section>

      {/* NARRATIVE + IMAGE */}
      <section className="bg-silk py-20 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Our Story</p>
            <h2 className="mt-3 font-syne text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              A studio built on one promise: <span className="bg-gradient-to-r from-spark to-ember bg-clip-text text-transparent">{SITE.tagline}.</span>
            </h2>
            <div className="mt-6 space-y-4 text-sm leading-relaxed text-slate-600 sm:text-base">
              <p>
                We started SparkGrid because we kept seeing the same story: brilliant Indian businesses
                settling for generic software. Templates passed off as design. Code that collapsed the
                moment real users showed up.
              </p>
              <p>
                So we built the studio we wished existed — one where designers sit with engineers from
                day one, where "done" means measured outcomes, and where every client gets our best
                people, not whoever happened to be free.
              </p>
              <p>
                Today our grid spans websites, web apps, mobile apps, crypto products and fully custom
                builds — each one shipped with the same signature: kinetic, precise, unmistakably premium.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-spark/20 to-ember/20 blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1629904853893-c2c8981a1dc5?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="SparkGrid Solutions studio workspace"
                loading="lazy"
                data-testid="about-studio-image"
                className="relative aspect-[4/3] w-full rounded-[2rem] border border-ink/10 object-cover shadow-2xl shadow-ink/10"
              />
              <div className="absolute -bottom-6 -left-4 flex items-center gap-3 rounded-2xl border border-ink/10 bg-white px-5 py-4 shadow-xl sm:-left-8">
                <MapPin className="h-5 w-5 text-ember" />
                <div>
                  <p className="font-syne text-sm font-bold text-ink">Mumbai, India</p>
                  <p className="font-mono text-xs text-slate-500">HQ · Serving worldwide</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-t border-ink/10 bg-white py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Engineering Principles</p>
            <h2 className="mt-3 font-syne text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">How we think.</h2>
          </Reveal>
          <div className="mt-10">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.08}>
                <div className="group grid gap-4 border-t border-ink/10 py-10 transition-colors duration-500 hover:bg-silk sm:grid-cols-12 sm:items-center sm:gap-8 lg:py-12">
                  <span className="text-outline-ink font-syne text-6xl font-extrabold sm:col-span-2 lg:text-7xl">{p.num}</span>
                  <div className="flex items-center gap-4 sm:col-span-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-night text-spark transition-transform duration-500 group-hover:scale-110">
                      <p.icon className="h-5 w-5" />
                    </span>
                    <h3 className="font-syne text-xl font-bold text-ink lg:text-2xl">{p.title}</h3>
                  </div>
                  <p className="text-sm leading-relaxed text-slate-500 sm:col-span-6 sm:text-base">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="grain relative overflow-hidden bg-night py-20 lg:py-32">
        <div className="pointer-events-none absolute -left-32 bottom-0 h-80 w-80 rounded-full bg-spark/10 blur-[110px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-spark">The Journey</p>
            <h2 className="mt-3 font-syne text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
              From spark to grid.
            </h2>
          </Reveal>
          <div className="mt-14 space-y-0">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.06}>
                <div className="group grid gap-3 border-t border-white/10 py-8 sm:grid-cols-12 sm:gap-8">
                  <span className="bg-gradient-to-r from-spark to-ember bg-clip-text font-syne text-4xl font-extrabold text-transparent sm:col-span-3 lg:text-5xl">
                    {t.year}
                  </span>
                  <h3 className="font-syne text-xl font-bold text-white sm:col-span-3">{t.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-400 sm:col-span-6 sm:text-base">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="bg-silk py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">Leadership</p>
            <h2 className="mt-3 font-syne text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
              The minds behind the grid.
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TEAM.map((m, i) => (
              <Reveal key={m.role} delay={i * 0.07}>
                <div className="group h-full rounded-3xl border border-ink/10 bg-white p-7 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-ink/10">
                  <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-spark to-ember text-night transition-transform duration-500 group-hover:scale-110 group-hover:rotate-6">
                    <m.icon className="h-7 w-7" />
                  </span>
                  <h3 className="mt-5 font-syne text-lg font-bold text-ink">{m.role}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{m.focus}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
